import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { DateTime, Duration } from "luxon";
import { EMPTY, Observable, ReplaySubject, Subject } from "rxjs";
import { map, switchMap, takeUntil, tap } from "rxjs/operators";
import { IUser } from "src/app/admin/users/models/IUser";
import { Timer, TimerResponse, TimerStatus } from "../components/timers/timer";
import { CurrentUserService } from "./current-user.service";

const timerUrl = "api/timer";

@Injectable({
  providedIn: "root",
})
export class TimerService implements OnDestroy {
  private destroy = new Subject();
  private timerSubject = new ReplaySubject<Timer>(1);

  private user: IUser = null;
  timer$ = this.timerSubject.asObservable();

  constructor(private http: HttpClient, currentUser: CurrentUserService) {
    currentUser.currentUserData$
      .pipe(
        takeUntil(this.destroy),
        tap((user) => (this.user = user)),
        switchMap((user) => (user ? this.getTimerForUser(user.id) : EMPTY))
      )
      .subscribe((timer) => {
        this.timerSubject.next(timer);
      });
  }
  ngOnDestroy() {
    this.destroy.next();
  }
  startTimer() {
    this.postTimer(`${timerUrl}/${this.user.id}/start`, {}).subscribe((timer) =>
      this.timerSubject.next(timer)
    );
  }
  pauseTimer() {
    this.postTimer(`${timerUrl}/${this.user.id}/pause`, {}).subscribe((timer) =>
      this.timerSubject.next(timer)
    );
  }
  resumeTimer() {
    this.postTimer(
      `${timerUrl}/${this.user.id}/resume`,
      {}
    ).subscribe((timer) => this.timerSubject.next(timer));
  }
  stopTimer() {
    this.postTimer(`${timerUrl}/${this.user.id}/stop`, {}).subscribe((timer) =>
      this.timerSubject.next(timer)
    );
  }
  updateTimerProject(projectId: number) {
    this.postTimer(
      `${timerUrl}/${this.user.id}`,
      projectId
    ).subscribe((timer) => this.timerSubject.next(timer));
  }
  postTimer(url: string, data: any): Observable<Timer> {
    return this.http
      .post<TimerResponse>(url, data)
      .pipe(map(this.mapResponseToTimer));
  }
  private getTimerForUser(userId: number): Observable<Timer> {
    return this.http
      .get<TimerResponse>(`${timerUrl}/${userId}`)
      .pipe(map(this.mapResponseToTimer));
  }
  private mapResponseToTimer(data: TimerResponse): Timer {
    var timer: Timer = {
      projectId: data.forProjectId,
      created: DateTime.fromISO(data.created),
      start: DateTime.fromISO(data.start),
      status: data.status,
      minutesTimed: data.minutesTimed,
    };

    // start with any previously saved time (i.e. periods of time before a pause)
    let currentMillis = data.minutesTimed * 60 * 1000;

    // if timer is active, get time since last start
    if (timer.status == TimerStatus.Active) {
      currentMillis += DateTime.local().toMillis() - timer.start.toMillis();
    }

    const duration = Duration.fromMillis(currentMillis);

    timer.elapsedTimeDisplay = duration.toFormat("hh:mm");
    return timer;
  }
}
